import type {
  AccountingFrame,
  ExpDisplayUpdate,
  GameEvent,
  GameStateSnapshot,
  SessionJourney,
  SessionJourneyContinuity,
  SessionJourneyEvent,
  SessionJourneyEventType,
  SessionJourneyPoint,
  SessionRecord,
} from '@/domain/types'
import { SESSION_JOURNEY_VERSION } from '@/domain/types'
import { EXP_JOURNEY_REVIEW_MS } from './session-exp-counter'

export const JOURNEY_INITIAL_BUCKET_MS = 30_000
export const JOURNEY_MAX_POINTS = 2_048
export const JOURNEY_RATE_WINDOW_MS = 5 * 60_000
export const JOURNEY_RATE_MINIMUM_MS = 60_000
const HOUR_MS = 3_600_000

export interface SessionJourneyRatePoint {
  readonly activeDurationMs: number
  readonly value: number | null
  readonly approximate: boolean
  readonly source: SessionJourneyPoint
}

export function deriveJourneyRatePoints(
  points: readonly SessionJourneyPoint[],
  windowMs = JOURNEY_RATE_WINDOW_MS,
  minimumMs = JOURNEY_RATE_MINIMUM_MS,
  version = 1,
): SessionJourneyRatePoint[] {
  let segmentStart = 0
  let anchorIndex = 0
  // Imported/legacy data need not be ordered. Keep the original scan for it.
  const ordered = points.every((point, index) => Number.isFinite(point.activeDurationMs)
    && (index === 0 || point.activeDurationMs >= points[index - 1]!.activeDurationMs))
  return points.map((point, index) => {
    if (index === 0 || point.continuity !== 'contiguous') {
      segmentStart = index
      anchorIndex = index
    }
    const cutoff = point.activeDurationMs - windowMs
    if (!ordered) anchorIndex = segmentStart
    while (anchorIndex < index && points[anchorIndex + 1]!.activeDurationMs <= cutoff) anchorIndex += 1
    const anchor = points[anchorIndex]!
    const durationMs = point.activeDurationMs - anchor.activeDurationMs
    const delta = point.expGained - anchor.expGained
    const value = index > anchorIndex && durationMs >= minimumMs && (version >= 2 || delta >= 0)
      ? delta / durationMs * HOUR_MS
      : null
    return {
      activeDurationMs: point.activeDurationMs,
      value: value !== null && Number.isFinite(value) ? value : null,
      approximate: anchor.level !== point.level && (anchor.approximate || point.approximate),
      source: point,
    }
  })
}

export class SessionJourneyRecorder {
  private currentJourney: SessionJourney | null = null
  private pendingPoints: SessionJourneyPoint[] = []
  private pendingGap = false
  private resolvedTransition: string | null = null

  start(record: SessionRecord, initialState: GameStateSnapshot | null): SessionJourney {
    this.pendingPoints = []
    this.pendingGap = false
    this.resolvedTransition = null
    const point = this.initialPoint(record, initialState)
    this.currentJourney = {
      sessionId: record.id,
      version: SESSION_JOURNEY_VERSION,
      startedAt: record.startedAt,
      updatedAt: record.startedAt,
      bucketSizeMs: JOURNEY_INITIAL_BUCKET_MS,
      points: point ? [point] : [],
      events: [],
    }
    return this.currentJourney
  }

  ingest(
    record: SessionRecord,
    accounting: AccountingFrame,
    events: readonly GameEvent[],
    state: GameStateSnapshot,
    display?: ExpDisplayUpdate,
  ): SessionJourney | null {
    if (!this.currentJourney || this.currentJourney.sessionId !== record.id) return this.currentJourney
    this.expirePending(state.capturedAt)
    let journey = this.appendGameEvents(this.currentJourney, record, events)
    if (display) {
      this.currentJourney = this.ingestDisplay(journey, record, state, display)
      return this.currentJourney
    }
    const update = accounting.exp
    if (update.exact
      && update.end
      && update.sourceEndAt !== null
      && update.activeEndAt !== null
      && record.aggregate.expGained !== null
      && Number.isFinite(record.aggregate.expGained)) {
      const previous = journey.points.at(-1)
      const continuity = journeyContinuity(update.continuity)
      const levelChanged = previous !== undefined && previous.level !== update.end.level
      const point: SessionJourneyPoint = {
        activeDurationMs: Math.max(0, update.activeEndAt),
        capturedAt: update.sourceEndAt,
        expGained: record.aggregate.expGained,
        level: update.end.level,
        expAbsolute: update.end.absolute,
        expRatio: update.end.ratio,
        mapName: this.mapAt(journey, update.sourceEndAt, state.mapName),
        approximate: record.aggregate.expGainedApproximate === true,
        continuity,
        anchor: continuity !== 'contiguous' || levelChanged,
      }
      const journeyWithPoint = this.appendPoint(journey, point)
      const pointAdded = journeyWithPoint !== journey
      journey = journeyWithPoint
      if (pointAdded && levelChanged && previous && !hasLevelEvent(journey.events, previous.level, point.level, point.capturedAt)) {
        journey = this.appendEvent(journey, {
          type: 'level-up',
          activeDurationMs: point.activeDurationMs,
          capturedAt: point.capturedAt,
          fromLevel: previous.level,
          toLevel: point.level,
        })
      }
      if (pointAdded && continuity === 'rebaseline') {
        journey = this.appendEvent(journey, {
          type: 'rebaseline',
          activeDurationMs: point.activeDurationMs,
          capturedAt: point.capturedAt,
        })
      }
    }
    this.currentJourney = { ...journey, updatedAt: Math.max(journey.updatedAt, state.capturedAt) }
    return this.currentJourney
  }

  mark(type: Extract<SessionJourneyEventType, 'pause' | 'resume' | 'capture-interrupted' | 'capture-restored'>, record: SessionRecord, capturedAt: number): SessionJourney | null {
    if (!this.currentJourney || this.currentJourney.sessionId !== record.id) return this.currentJourney
    this.closePending()
    this.currentJourney = this.appendEvent(this.currentJourney, {
      type,
      activeDurationMs: record.activeDurationMs,
      capturedAt,
    })
    return this.currentJourney
  }

  finish(record: SessionRecord, capturedAt: number): SessionJourney | null {
    if (!this.currentJourney || this.currentJourney.sessionId !== record.id) return this.currentJourney
    this.closePending()
    this.currentJourney = { ...this.currentJourney, updatedAt: Math.max(this.currentJourney.updatedAt, capturedAt) }
    return this.currentJourney
  }

  breakContinuity(): void {
    this.closePending()
    this.pendingGap = true
  }

  snapshot(): SessionJourney | null {
    return this.currentJourney
  }

  expirePending(now: number): void {
    if (this.currentJourney?.pendingExpSince !== undefined
      && now - this.currentJourney.pendingExpSince > EXP_JOURNEY_REVIEW_MS) {
      this.pendingPoints = []
      this.pendingGap = true
    }
  }

  private closePending(): void {
    if (this.currentJourney?.pendingExpSince !== undefined) this.pendingGap = true
    this.pendingPoints = []
    if (this.currentJourney) this.currentJourney = { ...this.currentJourney, pendingExpSince: undefined }
  }

  private ingestDisplay(journey: SessionJourney, record: SessionRecord, state: GameStateSnapshot, display: ExpDisplayUpdate): SessionJourney {
    const transition = display.journeyTransition
    journey = { ...journey, updatedAt: Math.max(journey.updatedAt, state.capturedAt) }
    if (display.continuity === 'discontinuous' && !transition) {
      this.pendingPoints = []
      this.pendingGap = true
      journey = { ...journey, pendingExpSince: undefined }
    }
    const point: SessionJourneyPoint | null = display.point && display.sourceAt !== null ? {
      capturedAt: display.sourceAt,
      activeDurationMs: Math.max(0, record.activeDurationMs - Math.max(0, state.capturedAt - display.sourceAt)),
      expGained: display.netGain ?? display.pendingNetGain ?? 0,
      level: display.point.level, expAbsolute: display.point.absolute, expRatio: display.point.ratio,
      approximate: display.approximate,
      mapName: this.mapAt(journey, display.sourceAt, state.mapName),
      continuity: display.continuity, anchor: display.continuity !== 'contiguous',
    } : null
    if (transition?.status === 'pending') {
      if (point && point.capturedAt < transition.fromSourceAt && display.pendingNetGain != null) {
        journey = this.appendPoint(journey, point)
      }
      for (const entry of journey.points) {
        if (entry.capturedAt >= transition.fromSourceAt && !this.pendingPoints.some(saved => saved.capturedAt === entry.capturedAt)) {
          this.pendingPoints.push(entry)
        }
      }
      if (point && point.capturedAt >= transition.fromSourceAt
        && !this.pendingPoints.some(entry => entry.capturedAt === point.capturedAt)) {
        this.pendingPoints.push(point)
      }
      return { ...journey, points: journey.points.filter(entry => entry.capturedAt < transition.fromSourceAt),
        pendingExpSince: transition.fromSourceAt }
    }
    if (transition?.status === 'unavailable') {
      this.pendingPoints = []
      this.pendingGap = true
      return { ...journey, pendingExpSince: transition.fromSourceAt }
    }
    if (!point || display.netGain === null) return journey

    const resolution = transition ? `${transition.id ?? transition.fromSourceAt}:${transition.revision ?? 0}:${transition.toSourceAt}:${transition.status}` : null
    if (transition?.status === 'confirmed' && resolution !== this.resolvedTransition) {
      // Apply the counter's confirmed offset to every withheld source frame before bucketing.
      const buffered = [...new Map([...journey.points, ...this.pendingPoints, point]
        .map(entry => [entry.capturedAt, entry])).values()]
        .filter(entry => entry.capturedAt >= transition.fromSourceAt && entry.capturedAt <= transition.toSourceAt)
        .sort((left, right) => left.capturedAt - right.capturedAt)
      journey = { ...journey, points: journey.points.filter(entry => entry.capturedAt < transition.fromSourceAt
        || entry.capturedAt > transition.toSourceAt), pendingExpSince: undefined }
      for (const entry of buffered) {
        const previous = journey.points.at(-1)
        const continuity = this.pendingGap ? 'discontinuous' : 'contiguous'
        const corrected = entry.capturedAt >= (transition.correctionFromSourceAt ?? transition.fromSourceAt)
        const level = corrected ? transition.level! : entry.level
        journey = this.appendPoint(journey, { ...entry,
          expGained: corrected ? entry.expAbsolute + transition.netOffset! : entry.expGained, level,
          approximate: corrected ? display.approximate : entry.approximate, continuity,
          anchor: continuity !== 'contiguous' || previous?.level !== level,
        })
        this.pendingGap = false
      }
      const first = buffered.find(entry => entry.capturedAt >= (transition.correctionFromSourceAt ?? transition.fromSourceAt))
      if (first && transition.levelUp) journey = this.appendEvent(journey, {
        type: 'level-up', capturedAt: first.capturedAt, activeDurationMs: first.activeDurationMs,
        fromLevel: transition.levelUp.from, toLevel: transition.levelUp.to,
      })
      this.pendingPoints = []
      this.resolvedTransition = resolution
      return journey
    }
    if (transition?.status === 'discarded') {
      this.pendingPoints = []
      this.resolvedTransition = resolution
    }
    const previous = journey.points.at(-1)
    if (previous && point.capturedAt < previous.capturedAt) return journey
    const continuity = this.pendingGap ? 'discontinuous' : point.continuity
    journey = this.appendPoint({ ...journey, pendingExpSince: undefined }, { ...point, continuity,
      anchor: continuity !== 'contiguous' || previous?.level !== point.level })
    this.pendingGap = false
    if (display.levelUp && previous && !hasLevelEvent(journey.events, display.levelUp.from, display.levelUp.to, point.capturedAt)) {
      journey = this.appendEvent(journey, { type: 'level-up', capturedAt: point.capturedAt,
        activeDurationMs: point.activeDurationMs, fromLevel: display.levelUp.from, toLevel: display.levelUp.to })
    }
    return journey
  }

  private initialPoint(record: SessionRecord, state: GameStateSnapshot | null): SessionJourneyPoint | null {
    if (!state
      || state.level === null
      || state.exp.absolute === null
      || state.exp.ratio === null
      || state.exp.estimated
      || !Number.isFinite(state.exp.ratio)) return null
    return {
      activeDurationMs: 0,
      capturedAt: state.observedAt?.exp ?? state.capturedAt,
      expGained: record.aggregate.expGained ?? 0,
      level: state.level,
      expAbsolute: state.exp.absolute,
      expRatio: state.exp.ratio,
      mapName: state.mapName,
      approximate: false,
      continuity: 'baseline',
      anchor: true,
    }
  }

  private appendGameEvents(journey: SessionJourney, record: SessionRecord, events: readonly GameEvent[]): SessionJourney {
    return events.reduce((current, event) => {
      if (event.type === 'map-changed') {
        return this.appendEvent(current, {
          type: 'map-change',
          activeDurationMs: record.activeDurationMs,
          capturedAt: event.capturedAt,
          ...(event.previousMapName ? { previousMapName: event.previousMapName } : {}),
          ...(event.mapName ? { mapName: event.mapName } : {}),
        })
      }
      return current
    }, journey)
  }

  private appendEvent(journey: SessionJourney, event: SessionJourneyEvent): SessionJourney {
    const duplicate = journey.events.some((candidate) => sameEvent(candidate, event))
    if (duplicate) return journey
    return {
      ...journey,
      updatedAt: Math.max(journey.updatedAt, event.capturedAt),
      events: [...journey.events, event].sort((left, right) => (
        left.activeDurationMs - right.activeDurationMs || left.capturedAt - right.capturedAt
      )),
    }
  }

  private appendPoint(journey: SessionJourney, point: SessionJourneyPoint): SessionJourney {
    const existingIndex = journey.points.findIndex((candidate) => candidate.capturedAt === point.capturedAt)
    if (existingIndex >= 0) {
      const previous = journey.points[existingIndex]!
      if (journey.version < 2 || previous.level === point.level) return journey
      const points = [...journey.points]
      points[existingIndex] = point
      return { ...journey, points }
    }

    const last = journey.points.at(-1)
    let points: SessionJourneyPoint[]
    if (last
      && !last.anchor
      && !point.anchor
      && Math.floor(last.activeDurationMs / journey.bucketSizeMs) === Math.floor(point.activeDurationMs / journey.bucketSizeMs)) {
      points = [...journey.points.slice(0, -1), point]
    } else {
      points = [...journey.points, point]
    }
    return compactJourney({ ...journey, points: sortPoints(points) })
  }

  private mapAt(journey: SessionJourney, capturedAt: number, fallback: string | null): string | null {
    let mapName = journey.points.at(-1)?.mapName ?? null
    for (const event of journey.events) {
      if (event.type === 'map-change' && event.capturedAt <= capturedAt && event.mapName) mapName = event.mapName
    }
    return mapName ?? fallback
  }
}

function journeyContinuity(value: AccountingFrame['exp']['continuity']): SessionJourneyContinuity {
  if (value === 'contiguous' || value === 'discontinuous' || value === 'rebaseline') return value
  return 'baseline'
}

function sameEvent(left: SessionJourneyEvent, right: SessionJourneyEvent): boolean {
  return left.type === right.type
    && left.capturedAt === right.capturedAt
    && left.fromLevel === right.fromLevel
    && left.toLevel === right.toLevel
    && left.previousMapName === right.previousMapName
    && left.mapName === right.mapName
}

function hasLevelEvent(events: readonly SessionJourneyEvent[], fromLevel: number, toLevel: number, capturedAt: number): boolean {
  return events.some((event) => event.type === 'level-up'
    && event.fromLevel === fromLevel
    && event.toLevel === toLevel
    && Math.abs(event.capturedAt - capturedAt) <= 15_000)
}

function sortPoints(points: readonly SessionJourneyPoint[]): SessionJourneyPoint[] {
  return [...points].sort((left, right) => (
    left.activeDurationMs - right.activeDurationMs || left.capturedAt - right.capturedAt
  ))
}

function compactJourney(journey: SessionJourney): SessionJourney {
  let bucketSizeMs = journey.bucketSizeMs
  let points = [...journey.points]
  while (points.length > JOURNEY_MAX_POINTS) {
    bucketSizeMs *= 2
    const buckets = new Map<number, SessionJourneyPoint>()
    const anchors: SessionJourneyPoint[] = []
    for (const point of points) {
      if (point.anchor) anchors.push(point)
      else buckets.set(Math.floor(point.activeDurationMs / bucketSizeMs), point)
    }
    const compacted = sortPoints([...anchors, ...buckets.values()])
    if (compacted.length >= points.length) {
      points = evenlySample(compacted, JOURNEY_MAX_POINTS)
      break
    }
    points = compacted
  }
  return { ...journey, bucketSizeMs, points }
}

function evenlySample(points: readonly SessionJourneyPoint[], limit: number): SessionJourneyPoint[] {
  if (points.length <= limit) return [...points]
  const result = [points[0]!]
  const interior = limit - 2
  for (let index = 1; index <= interior; index += 1) {
    result.push(points[Math.round(index * (points.length - 1) / (limit - 1))]!)
  }
  result.push(points.at(-1)!)
  return sortPoints([...new Map(result.map((point) => [point.capturedAt, point])).values()])
}
